import { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import { isToday } from 'date-fns';

import type { Job } from 'src/types';

import translations from './.vocab';

export interface ActionStatus {
  inactive: boolean;
  inactiveDescription?: string;
}

export interface JobActionStatus {
  viewActionStatus: ActionStatus;
  editActionStatus: ActionStatus;
  notificationActionStatus: ActionStatus;
  teamAccessActionStatus: ActionStatus;
  shareActionStatus: ActionStatus;
  refreshActionStatus: ActionStatus;
  copyActionStatus: ActionStatus;
  expireActionStatus: ActionStatus;
  viewJobInfoActionStatus: ActionStatus;
  multiLocationActionStatus: ActionStatus;
}

interface EditActionStatusOptions {
  allowForIndirectJob: boolean;
  editWhileExpired: boolean;
}

export const ACTIVE = { inactive: false };
export const INACTIVE = { inactive: true };

export const getDisableStatus = (disableDescription: string): ActionStatus => ({
  inactive: true,
  inactiveDescription: disableDescription,
});

const useGetEditActionStatus = ({
  job,
  showOnCreditHoldMessage,
  isCsView,
  actionText,
  options = {
    allowForIndirectJob: false,
    editWhileExpired: false,
  },
}: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  isCsView: boolean;
  actionText?: string;
  options?: EditActionStatusOptions;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  const action = actionText ?? t('edit');

  if (!options.allowForIndirectJob && job.postType === 'INDIRECT') {
    return INACTIVE;
  }

  if (showOnCreditHoldMessage && !isCsView) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status) &&
    !isCsView
  ) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (!options.editWhileExpired && job.status === 'EXPIRED') {
    return getDisableStatus(t('Expired ads cannot be ...ed', { action }));
  }

  return ACTIVE;
};

const useGetNotificationActionStatus = (params: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  isCsView: boolean;
}): ActionStatus => useGetEditActionStatus(params);

const useGetTeamAccessActionStatus = (params: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  isCsView: boolean;
}): ActionStatus =>
  useGetEditActionStatus({
    ...params,
    options: {
      allowForIndirectJob: true,
      editWhileExpired: true,
    },
  });

const isListedToday = (job: Job) => {
  const { listingDate } = job;

  if (!listingDate) {
    return false;
  }

  return isToday(new Date(listingDate));
};

const useGetRefreshActionStatus = ({
  job,
  showOnCreditHoldMessage,
  canCreateJobs,
}: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  canCreateJobs: boolean;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  if (job.postType === 'INDIRECT') {
    return INACTIVE;
  }

  if (!job.canBeRefreshed) {
    return getDisableStatus(t('Refresh is not available for Premium ads'));
  }

  if (showOnCreditHoldMessage) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (!canCreateJobs) {
    return getDisableStatus(
      t('For access to ... ads contact your account Administrator', {
        action: t('refresh'),
      }),
    );
  }

  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status)
  ) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (job.status === 'EXPIRED') {
    return getDisableStatus(
      t('Expired ads cannot be ...ed', { action: t('refresh') }),
    );
  }

  if (isListedToday(job)) {
    return getDisableStatus(
      t('This ad has already been posted or refreshed today'),
    );
  }

  return ACTIVE;
};

const useGetShareActionStatus = ({
  job,
  showOnCreditHoldMessage,
  canCreateJobs,
}: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  canCreateJobs: boolean;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  if (showOnCreditHoldMessage) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (!canCreateJobs) {
    return getDisableStatus(
      t('For access to ... ads contact your account Administrator', {
        action: t('share'),
      }),
    );
  }

  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status)
  ) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (job.status === 'EXPIRED') {
    return getDisableStatus(t('Expired ads cannot be shared'));
  }

  return ACTIVE;
};

const useGetMultiLocationActionStatus = ({
  job,
  showOnCreditHoldMessage,
  canCreateJobs,
}: {
  job: Job;
  showOnCreditHoldMessage: boolean;
  canCreateJobs: boolean;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  if (showOnCreditHoldMessage) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (!canCreateJobs) {
    return getDisableStatus(
      t('To post jobs in more locations contact your account Administrator'),
    );
  }

  const isPending = [
    'PENDING',
    'PENDING_ACCOUNT_APPROVAL',
    'SUSPENDED',
  ].includes(job.status);

  if (isPending) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (job.status === 'EXPIRED') {
    return getDisableStatus(
      t('Expired ads cannot be posted to additional locations'),
    );
  }

  return ACTIVE;
};

const useGetViewActionStatus = (job: Job): ActionStatus => {
  const { t } = useTranslations(translations);
  if (['ACTIVE', 'EXPIRED'].includes(job.status)) {
    return ACTIVE;
  }

  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status)
  ) {
    return getDisableStatus(t('Pending jobs cannot be viewed'));
  }

  return INACTIVE;
};

const useGetViewJobInfoActionStatus = ({ job }: { job: Job }): ActionStatus => {
  const { t } = useTranslations(translations);
  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status)
  ) {
    return getDisableStatus(t('Available once the job has been live'));
  }

  return ACTIVE;
};

const useGetCopyActionStatus = ({
  showOnCreditHoldMessage,
  canCreateJobs,
}: {
  showOnCreditHoldMessage: boolean;
  canCreateJobs: boolean;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  if (showOnCreditHoldMessage) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (!canCreateJobs) {
    return getDisableStatus(
      t('For access to ... ads contact your account Administrator', {
        action: t('copy'),
      }),
    );
  }

  return ACTIVE;
};

const useGetExpireActionStatus = ({
  job,
  isCsView,
}: {
  job: Job;
  isCsView: boolean;
}): ActionStatus => {
  const { t } = useTranslations(translations);

  if (job.postType === 'INDIRECT') {
    return INACTIVE;
  }

  if (
    ['PENDING', 'PENDING_ACCOUNT_APPROVAL', 'SUSPENDED'].includes(job.status) &&
    !isCsView
  ) {
    return getDisableStatus(t('Available once the job is live'));
  }

  if (job.status === 'EXPIRED') {
    return getDisableStatus(t('This ad is already expired'));
  }

  return ACTIVE;
};

export const useGetJobActionStatus = (job: Job): JobActionStatus => {
  const { accountContext } = useAccountContext();
  const showOnCreditHoldMessage =
    accountContext?.headerFooterContext?.advertiser?.showOnCreditHoldMessage ??
    false;
  const isCsView =
    accountContext?.headerFooterContext?.identityContext?.act?.isCsView ??
    false;

  return {
    viewActionStatus: useGetViewActionStatus(job),
    editActionStatus: useGetEditActionStatus({
      job,
      showOnCreditHoldMessage,
      isCsView,
    }),
    notificationActionStatus: useGetNotificationActionStatus({
      job,
      isCsView,
      showOnCreditHoldMessage,
    }),
    teamAccessActionStatus: useGetTeamAccessActionStatus({
      job,
      isCsView,
      showOnCreditHoldMessage,
    }),
    shareActionStatus: useGetShareActionStatus({
      job,
      showOnCreditHoldMessage,
      canCreateJobs:
        accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs ??
        false,
    }),
    refreshActionStatus: useGetRefreshActionStatus({
      job,
      showOnCreditHoldMessage,
      canCreateJobs:
        accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs ??
        false,
    }),
    copyActionStatus: useGetCopyActionStatus({
      showOnCreditHoldMessage,
      canCreateJobs:
        accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs ??
        false,
    }),
    expireActionStatus: useGetExpireActionStatus({
      job,
      isCsView,
    }),
    viewJobInfoActionStatus: useGetViewJobInfoActionStatus({
      job,
    }),
    multiLocationActionStatus: useGetMultiLocationActionStatus({
      job,
      showOnCreditHoldMessage,
      canCreateJobs:
        accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs ??
        false,
    }),
  };
};
